import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import { usePermissions } from '../../hooks/usePermissions';
import usePagePermissions from '../../hooks/usePagePermissions';
import { Box, CircularProgress, Alert } from '@mui/material';

export default function ProtectedRoute({
  children,
  requiredPermission,
  requiredRole,
  adminOnly = false,
  requiredPage = null,
  fallbackPath = '/dashboard'
}) {
  const { isAuthenticated, loading, user } = useAppSelector((state) => state.auth);
  const { hasPermission } = usePermissions();
  const { canAccessPage } = usePagePermissions();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh"
        }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  //  FIXED: Allow both admin and super_admin access
  if (adminOnly && !['admin', 'super_admin'].includes(user?.role)) {
    return (
      <Alert severity="error">
        Access denied. Administrator privileges required to access this page.
        <br />
        Current user: {user?.username} ({user?.role})
      </Alert>
    );
  }

  // Check role-based access
  if (requiredRole && user?.role !== requiredRole && user?.role !== 'super_admin') {
    return (
      <Alert severity="error">
        Access denied. You don't have the required role to access this page.
      </Alert>
    );
  }

  // Check permission-based access
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <Alert severity="error">
        Access denied. You don't have the required permissions to access this page.
      </Alert>
    );
  }

  // Check page-level access based on pagePermissions
  if (requiredPage && !['admin', 'super_admin'].includes(user?.role)) {
    if (!canAccessPage(requiredPage)) {
      return (
        <Alert severity="error">
          Access denied. You don't have access to this page.
          <br />
          Current user: {user?.username} ({user?.role})
        </Alert>
      );
    }
  }

  return children;
}

// HOC for role-based protection
export const withRoleProtection = (Component, allowedRoles) => {
  return function ProtectedComponent(props) {
    const { user } = useAppSelector((state) => state.auth);
    
    if (!allowedRoles.includes(user?.role) && user?.role !== 'super_admin') {
      return (
        <Alert severity="error">
          Access denied. Your role ({user?.role}) is not authorized to access this feature.
        </Alert>
      );
    }

    return <Component {...props} />;
  };
};

//  FIXED: Permission-based protection with super_admin bypass
export const withPermissionProtection = (Component, requiredPermissions) => {
  return function ProtectedComponent(props) {
    const { user } = useAppSelector((state) => state.auth);
    const { hasAnyPermission } = usePermissions();
    
    // Super admin bypasses all permission checks
    if (user?.role === 'super_admin') {
      return <Component {...props} />;
    }

    if (!hasAnyPermission(requiredPermissions)) {
      return (
        <Alert severity="error">
          Access denied. You don't have the required permissions to access this feature.
        </Alert>
      );
    }

    return <Component {...props} />;
  };
};
